import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { ReactNode } from 'react';
import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { AutocompleteBase, AutocompleteBaseProps } from '../autocomplete';
import { FlowIcon } from '../flow-icon/FlowIcon';
import { Text } from '../text';

// TODO(jacob): Remove once react-hook-form setValue types are compatible after upgrade.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactHookFormBypassType = any;

const ROOT = makeRootClassName('InlineAutocomplete');
const el = makeElementClassNameFactory(ROOT);

export type InlineAutocompleteOption = {
  id: string | number | boolean;
  label: string;
};

interface InlineAutocompleteProps<T> {
  label: string;
  labelSuffix?: ReactNode;
  name: Path<T>;
  save: (p: Path<T>) => void;
  required: boolean;
  options: InlineAutocompleteOption[];
  defaultValue?: InlineAutocompleteOption['id'];
  expandable?: boolean;
  customValidator?: (val: string) => boolean;
  autoCompleteProps?: Omit<
    AutocompleteBaseProps<InlineAutocompleteOption, false>,
    'onChange' | 'options' | 'value'
  >;
}

export const InlineAutocomplete = <T extends FieldValues>(
  p: InlineAutocompleteProps<T>
) => {
  const { watch, setValue } = useFormContext<T>();
  // NOTE(max): ?? '' is needed to avoid switching between undefined and a
  // defined value. When switching between those two, MUI gets confused as to
  // whether the component is controlled or uncontrolled. This can lead to
  // unexpected behavior.
  const value: InlineAutocompleteOption['id'] =
    watch(p.name) ?? p.defaultValue ?? '';

  const handleSelect = (option: InlineAutocompleteOption | null) => {
    setValue(p.name, option?.id as ReactHookFormBypassType);
    p.save(p.name);
  };

  const placeholder = `Add ${p.label.toLowerCase()}`;
  const selectedValue =
    p.options.find((o) => o.id === value)?.label ?? String(value);

  const isValid = (val: string) => {
    if (p.customValidator) {
      return p.customValidator(val);
    }
    return !isNil(val);
  };

  return (
    <div className={clsx(ROOT)}>
      <div className={el`table`}>
        <div className={el`label`}>
          <Text type="body-sm">{p.label}</Text>
          {p.labelSuffix}
        </div>
        <div className={el`field-and-icon-wrapper`}>
          <AutocompleteBase
            onChange={(_, value) => {
              handleSelect(value as InlineAutocompleteOption);
            }}
            options={p.options}
            inputProps={{
              required: p.required,
            }}
            value={selectedValue}
            selectOnFocus
            size="small"
            compact
            blurOnSelect
            transparentWithoutFocus
            inputWrapperClassName={clsx(
              el`input-wrapper`,
              isValid(selectedValue) ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholderClassName={clsx(
              el`placeholder`,
              isValid(selectedValue) ? 'VALID' : 'INVALID',
              p.required && 'REQUIRED'
            )}
            placeholder={placeholder}
            expandable={p.expandable}
            className={el`field-wrapper`}
            {...p.autoCompleteProps}
          />
          <FlowIcon fieldPath={p.name} />
        </div>
      </div>
    </div>
  );
};
